// Phase 0085-B — Assessment Template admin page.
// Lists templates; clicking a template expands sections + questions.

import { useState } from 'react'
import { App, Button, Card, Collapse, Empty, Form, Input, Modal, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQuery } from '@tanstack/react-query'

import { assessmentApi, type AssessmentQuestion, type AssessmentSection, type AssessmentTemplate } from '@/features/hr/assessmentApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

export default function AssessmentTemplatesPage() {
  const { message } = App.useApp()
  const [openId, setOpenId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: templates = [], isLoading, refetch } = useQuery({
    queryKey: ['assessment-templates'],
    queryFn: () => assessmentApi.listTemplates(),
  })

  const { data: bundle } = useQuery({
    queryKey: ['assessment-template-bundle', openId],
    queryFn: () => assessmentApi.getTemplate(openId!),
    enabled: !!openId,
  })

  const createMut = useMutation({
    mutationFn: (values: Partial<AssessmentTemplate>) => assessmentApi.createTemplate(values),
    onSuccess: () => { message.success('Template created'); setCreateOpen(false); form.resetFields(); refetch() },
    onError: (e: Error) => message.error(e.message),
  })

  const columns: ColumnsType<AssessmentTemplate> = [
    { title: 'Code', dataIndex: 'template_code', width: 260,
      render: (c: string) => <Text code>{c}</Text> },
    { title: 'Name', dataIndex: 'template_name' },
    { title: 'Default', dataIndex: 'is_default', width: 80,
      render: (b: boolean) => b ? <Tag color="gold">Default</Tag> : '' },
    { title: 'Version', dataIndex: 'version_no', width: 80 },
    { title: 'Status', dataIndex: 'approval_status', width: 120,
      render: (s: string) => <Tag color={s === 'APPROVED' ? 'green' : 'default'}>{s}</Tag> },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, r) => (
        <Button size="small" onClick={() => setOpenId(r.id)}>Open</Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Assessment Templates"
        subtitle="Configurable templates with sections + questions. The default template is auto-seeded."
        extra={<Button type="primary" onClick={() => setCreateOpen(true)}>New Template</Button>}
      />
      <Table rowKey="id" columns={columns} dataSource={templates}
             loading={isLoading} pagination={false} size="middle" />

      {openId && bundle && (
        <Card style={{ marginTop: 16 }}
              title={`${bundle.template_name} — ${bundle.sections?.length || 0} sections`}
              extra={<Button onClick={() => setOpenId(null)}>Close</Button>}>
          {(!bundle.sections || bundle.sections.length === 0) ? (
            <Empty />
          ) : (
            <Collapse defaultActiveKey={bundle.sections?.map((s) => s.id)}>
              {bundle.sections.map((s: AssessmentSection) => (
                <Collapse.Panel
                  key={s.id}
                  header={
                    <Space>
                      <Tag>{s.applies_to_role}</Tag>
                      <Text strong>{s.section_name}</Text>
                      <Text type="secondary">{(s.questions || []).length} questions</Text>
                    </Space>
                  }
                >
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={s.questions || []}
                    columns={[
                      { title: 'Code', dataIndex: 'question_code', width: 200,
                        render: (c: string) => <Text code style={{ fontSize: 11 }}>{c}</Text> },
                      { title: 'Question', dataIndex: 'question_text' },
                      { title: 'Type', dataIndex: 'question_type', width: 130 },
                      { title: 'Role', dataIndex: 'answerable_by_role', width: 100 },
                      { title: 'Weight', dataIndex: 'weight', width: 80 },
                      { title: 'Dimension', dataIndex: 'contributes_to_dimension', width: 180,
                        render: (d: string | null) => d || '—' },
                      { title: 'Auto KPI', dataIndex: 'auto_kpi_source', width: 160,
                        render: (s: string | null) => s ? <Tag color="blue">{s}</Tag> : '' },
                    ]}
                  />
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
        </Card>
      )}

      <Modal
        open={createOpen}
        title="Create Template"
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
      >
        <Form form={form} layout="vertical" onFinish={(v) => createMut.mutate(v)}>
          <Form.Item name="template_code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="template_name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
